/**
 * REAL upload actors (F3-F) to inject into `mediaUploadMachine` (F2-B).
 * Translate the machine's steps into control-plane calls (F3-E) and into
 * DIRECT browser→R2 PUTs of each part (data plane). The ETag is read from
 * the PUT response header — depends on `CORS ExposeHeaders: ETag` on the
 * bucket (without that, multipart `complete` breaks).
 *
 * PROGRESS is reported via an injected callback (`onProgress`) that the UI
 * wires to `actor.send({type:"PROGRESS", …})`, because a `fromPromise` actor
 * can't emit events to its parent on its own.
 */

import { fromPromise } from "xstate";
import type {
  AbortUploadInput,
  CompletedPart,
  CompleteUploadInput,
  CreateUploadInput,
  CreateUploadResult,
  UploadPartsInput,
  VideoAssetMeta,
} from "@space/shared";
import type { MediaApi } from "./media-api";

export interface UploadActorContext {
  /** Course/lesson/session the video is associated with (creation). */
  courseId?: string | null;
  lessonId?: string | null;
  classSessionId?: string | null;
}

/** PAUSE sentinel: `uploadParts` throws it at the part boundary when the UI
 * asks to pause → the machine goes to `failed`; the UI treats it as "paused" and RETRY
 * resumes from the already-uploaded parts (without aborting on the server). */
export const PAUSE_SENTINEL = "__paused__";

export interface UploadActorDeps {
  api: MediaApi;
  /** Asset association context (only on new creation). */
  context?: UploadActorContext;
  /** Notifies progress; the UI forwards it as a PROGRESS event to the machine. */
  onProgress?: (uploadedCount: number, uploadedParts: CompletedPart[]) => void;
  /** If it returns true, `uploadParts` pauses at the next part boundary. */
  shouldPause?: () => boolean;
}

/** Extracts the (clean) ETag from a part's PUT response. */
function readEtag(res: Response, partNumber: number): string {
  const raw = res.headers.get("ETag") ?? res.headers.get("etag");
  if (!raw) {
    throw new Error(
      `parte ${partNumber}: sin cabecera ETag (revisa CORS ExposeHeaders: ETag en el bucket)`,
    );
  }
  return raw.trim();
}

function missingPartNumbers(totalParts: number, done: CompletedPart[]): number[] {
  const doneSet = new Set(done.map((p) => p.partNumber));
  const out: number[] = [];
  for (let n = 1; n <= totalParts; n++) {
    if (!doneSet.has(n)) {
      out.push(n);
    }
  }
  return out;
}

function toFile(blob: unknown): Blob {
  if (blob instanceof Blob) {
    return blob;
  }
  throw new Error("upload: falta el File/Blob real (file.blob)");
}

/**
 * Builds the set of actors for `mediaUploadMachine.provide({ actors })`.
 */
export function createUploadActors(deps: UploadActorDeps) {
  const { api, context, onProgress, shouldPause } = deps;

  const createUpload = fromPromise(
    async ({ input }: { input: CreateUploadInput }): Promise<CreateUploadResult> => {
      // RESUME mode: list_parts to avoid re-uploading what's already up.
      if (input.resume) {
        const status = await api.getUpload(input.resume.uploadId);
        const partSize = status.partSizeBytes;
        const totalParts = Math.max(1, Math.ceil(status.sizeBytes / partSize));
        return {
          assetId: status.assetId,
          uploadId: status.uploadId,
          storageKey: "",
          partSize,
          totalParts,
          uploadedParts: status.uploadedParts,
        };
      }
      // New creation.
      const created = await api.createUpload({
        fileName: input.file.name,
        sizeBytes: input.file.size,
        contentType: input.file.type || "video/mp4",
        title: input.title,
        courseId: context?.courseId ?? null,
        lessonId: context?.lessonId ?? null,
        classSessionId: context?.classSessionId ?? null,
      });
      const partSize = created.partSizeBytes;
      const totalParts = Math.max(1, Math.ceil(input.file.size / partSize));
      return {
        assetId: created.assetId,
        uploadId: created.uploadId,
        storageKey: "",
        partSize,
        totalParts,
        uploadedParts: [],
      };
    },
  );

  const uploadParts = fromPromise(
    async ({
      input,
      signal,
    }: {
      input: UploadPartsInput;
      signal: AbortSignal;
    }): Promise<CompletedPart[]> => {
      const file = toFile(input.file.blob);
      const completed: CompletedPart[] = [...input.uploadedParts];
      const missing = missingPartNumbers(input.totalParts, completed);
      if (missing.length === 0) {
        return completed.toSorted((a, b) => a.partNumber - b.partNumber);
      }
      const urls = await api.partUrls(input.uploadId, missing);
      const urlByPart = new Map(urls.map((u) => [u.partNumber, u.url]));

      for (const partNumber of missing) {
        if (signal.aborted) {
          throw new DOMException("upload aborted", "AbortError");
        }
        if (shouldPause?.()) {
          throw new Error(PAUSE_SENTINEL);
        }
        const url = urlByPart.get(partNumber);
        if (!url) {
          throw new Error(`parte ${partNumber}: sin URL presignada`);
        }
        const start = (partNumber - 1) * input.partSize;
        const end = Math.min(start + input.partSize, file.size);
        const chunk = file.slice(start, end);
        const res = await fetch(url, { method: "PUT", body: chunk, signal });
        if (!res.ok) {
          throw new Error(`parte ${partNumber}: PUT HTTP ${res.status}`);
        }
        completed.push({ partNumber, etag: readEtag(res, partNumber) });
        onProgress?.(completed.length, [...completed]);
      }
      return completed.toSorted((a, b) => a.partNumber - b.partNumber);
    },
  );

  const completeUpload = fromPromise(
    async ({ input }: { input: CompleteUploadInput }): Promise<VideoAssetMeta> =>
      api.completeUpload(input.uploadId, input.parts),
  );

  const abortUpload = fromPromise(async ({ input }: { input: AbortUploadInput }): Promise<void> => {
    await api.abortUpload(input.uploadId);
  });

  return { createUpload, uploadParts, completeUpload, abortUpload };
}
